import React, { Component } from 'react';
import { Field } from 'redux-form';
import { FormattedMessage, injectIntl } from 'react-intl';

import CountryField from './country-field';
import FormField from '../shared/form-field';
import StateField from './state-field';
import CityField from './city-field';
import ZipCodeField from './zipcode-field';

import { AVAILABLES_FOR } from '../../../constants/app-chekout';
import validators from '../../shared/validators';
import APP_DEFAULTS from '../../../constants/app-defaults';
import ZoneService from '../../../services/zone-service';

class AddressFields extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loadingStates: true,
            loadingCities: !this.isBilling(),
            stateList: {
                states: [],
            },
            citiesList: {
                cities: [],
            },
            zipcodesList: {
                zipcodes: [],
            },
            selectedStateCode: this.props.selectedStateCode,
        };

        this.handleCountryChange = this.handleCountryChange.bind(this);
        this.handleStateChange = this.handleStateChange.bind(this);
        this.handleCityChange = this.handleCityChange.bind(this);
        this.getStatesForCountry = this.getStatesForCountry.bind(this);
        this.isBilling = this.isBilling.bind(this);
    }

    componentDidMount() {
        this.getAllCountries();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.selectedCountryIso !== prevProps.selectedCountryIso) {
            this.props.changeForm(`${this.props.fieldNamePrefix}[city]`, '');
            this.props.changeForm(`${this.props.fieldNamePrefix}[zipcode]`, '');
        }
    }

    getAllCountries() {
        const availableFor = !this.isBilling() ? AVAILABLES_FOR.shipping : AVAILABLES_FOR.billing;
        const shippingZone = !this.isBilling() ? this.props.currentZone : undefined;

        this.props.fetchCountries(availableFor, shippingZone).then(() => {
            this.getStatesForCountry(availableFor, this.props.selectedCountryIso, shippingZone);
        });
    }

    /**
     * Since the zone can have multiple states, we need to figure out the state for current municipality
     */
    getTargetStateBasedOnMunicipality(states) {
        const { relationships: { state: { data: { id: municipalityStateId } } } } = this.props.municipalityInfo;
        return states.find((state) => state.id === parseInt(municipalityStateId, 10));
    }

    getTargetZipcodeBasedOnMunicipality(municipalities) {
        const { attributes: { name } } = this.props.municipalityInfo;
        return municipalities.find((municipality) => municipality.name.trim().toUpperCase() === name.trim().toUpperCase());
    }

    getStatesForCountry(availableName, countryIso, currentZone) {
        if (!this.isBilling() && !currentZone) {
            this.getAllCountries();
            return;
        }

        const countries = this.isBilling() ? this.props.shippingCountries : this.props.billingCountries;
        const country = countries.find((country) => country.iso === countryIso);
        this.props.fetchStatesForCountry(country && country.id, availableName, currentZone).then((response) => {
            let selectedStateCode = null;
            if (currentZone && response.states.length && this.props.municipalityInfo) {
                if (response.states.length > 0) {
                    const targetState = this.getTargetStateBasedOnMunicipality(response.states);
                    selectedStateCode = targetState.abbr;
                } else {
                    selectedStateCode = response.states[0].abbr;
                }
            }
            this.setState((prevState) => ({
                ...prevState,
                loadingStates: false,
                stateList: {
                    states: response.states,
                },
                selectedStateCode: selectedStateCode || prevState.selectedStateCode,
            }));
            this.props.changeForm(`${this.props.fieldNamePrefix}[state_name]`, this.state.selectedStateCode);

            if (AVAILABLES_FOR.shipping === availableName) {
                this.props.fetchMunicipalitiesForCountryState(this.props.selectedCountryIso, this.state.selectedStateCode).then((response) => {
                    const cities = response.municipalities ? response.municipalities : [];

                    let zipcodes = [];
                    if (response.municipalities && response.municipalities.length > 0) {
                        const targetZipcode = this.getTargetZipcodeBasedOnMunicipality(response.municipalities);
                        if (targetZipcode) {
                            zipcodes = targetZipcode.zip_code;
                        } else {
                            zipcodes = response.municipalities[0].zip_code;
                        }
                    }

                    this.setState((prevState) => ({
                        ...prevState,
                        loadingCities: false,
                        citiesList: {
                            cities,
                        },
                        zipcodesList: {
                            zipcodes,
                        },
                    }));
                });
            }
        });
    }

    filterCities(cityName) {
        return this.state.citiesList.cities.filter((city) => cityName === city.name);
    }

    handleCountryChange(countryIso) {
        // Start loader
        this.setState({
            loadingStates: true,
        });
        this.getStatesForCountry(AVAILABLES_FOR.billing, countryIso);
    }

    // Load cities(municipalities) when country state change in shipping
    handleStateChange(stateCode) {
        if (!this.isBilling()) {
            // Start loader
            this.setState({
                loadingCities: true,
            });
            this.props.fetchMunicipalitiesForCountryState(this.props.selectedCountryIso, stateCode).then((response) => {
                const cities = response.municipalities ? response.municipalities : [];
                const zipcodes = response.municipalities && response.municipalities.length > 0 ? response.municipalities[0].zip_code : [];

                this.setState((prevState) => ({
                    ...prevState,
                    loadingCities: false,
                    citiesList: {
                        cities,
                    },
                    zipcodesList: {
                        zipcodes,
                    },
                }));
                this.props.changeForm(`${this.props.fieldNamePrefix}[state_name]`, stateCode);
            });
        }
        this.setState((prevState) => ({ ...prevState, selectedStateCode: stateCode }));
    }

    // Load zipcodes when city change in shipping
    handleCityChange(cityName) {
        const cities = this.filterCities(cityName);
        const zipcodes = cities.length > 0 ? cities[0].zip_code : [];
        this.setState((prevState) => ({ ...prevState, zipcodesList: { zipcodes } }));
        this.props.changeForm(`${this.props.fieldNamePrefix}[zipcode]`, zipcodes[0]);
    }

    isBilling() {
        return this.props.fieldNamePrefix === 'order[bill_address_attributes]';
    }

    render() {
        return (
            <div>
                {this.props.children}

                <div className="row">
                    <div className="col-md-6">
                        <Field
                            className="form-control"
                            name={`${this.props.fieldNamePrefix}[firstname]`}
                            component={FormField.inputFieldMarkup}
                            label={this.props.intl.formatMessage({ id: 'firstname', defaultMessage: 'First Name' })}
                            type="text"
                            validate={validators.required}
                        />
                    </div>
                    <div className="col-md-6">
                        <Field
                            className="form-control"
                            name={`${this.props.fieldNamePrefix}[lastname]`}
                            component={FormField.inputFieldMarkup}
                            label={this.props.intl.formatMessage({ id: 'lastname', defaultMessage: 'Last Name' })}
                            type="text"
                            validate={validators.required}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <Field
                            className="form-control"
                            name={`${this.props.fieldNamePrefix}[address1]`}
                            component={FormField.inputFieldMarkup}
                            label={this.props.intl.formatMessage({ id: 'address1', defaultMessage: 'Address Line 1' })}
                            type="text"
                            validate={validators.required}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <Field
                            className="form-control"
                            name={`${this.props.fieldNamePrefix}[address2]`}
                            component={FormField.inputFieldMarkup}
                            label={this.props.intl.formatMessage({ id: 'address2', defaultMessage: 'Address Line 2' })}
                            type="text"
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        {this.props.selectedCountryIso !== APP_DEFAULTS.countryIsoCuba ? (
                            <Field
                                className="form-control"
                                name={`${this.props.fieldNamePrefix}[city]`}
                                component={FormField.inputFieldMarkup}
                                label={this.props.intl.formatMessage({ id: 'city', defaultMessage: 'City' })}
                                type="text"
                                validate={validators.required}
                            />
                        ) : (
                            <div className="checkout-form-row">
                                <label htmlFor="city" className="checkout-form-label">
                                    <FormattedMessage id="municipality" defaultMessage="Municipality" />
                                </label>
                                <div className="checkout-form-fields">
                                    <Field
                                        name={`${this.props.fieldNamePrefix}[city]`}
                                        fieldNamePrefix={this.props.fieldNamePrefix}
                                        cities={this.state.citiesList.cities}
                                        component={CityField}
                                        handleCityChange={this.handleCityChange}
                                        displayLoader={this.state.loadingCities}
                                        validate={validators.required}
                                        noticeChange={{
                                            availableMunicipalities: this.props.availableMunicipalities,
                                            setCurrentMunicipality: this.props.setCurrentMunicipality,
                                            emptyCurrentCart: this.props.emptyCurrentCart,
                                            goToHome: this.props.goToHome,
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="col-md-4">
                        <div className="checkout-form-row">
                            <label htmlFor="country" className="checkout-form-label">
                                <FormattedMessage id="country" defaultMessage="Country" />
                            </label>
                            <div className="checkout-form-fields">
                                <Field
                                    name={`${this.props.fieldNamePrefix}[country_iso]`}
                                    fieldNamePrefix={this.props.fieldNamePrefix}
                                    countries={this.isBilling() ? this.props.shippingCountries : this.props.billingCountries}
                                    component={CountryField}
                                    handleCountryChange={this.handleCountryChange}
                                    selectedCountryIso={this.props.selectedCountryIso}
                                    validate={validators.required}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="checkout-form-row">
                            <label htmlFor="state" className="checkout-form-label">
                                <FormattedMessage id="state" defaultMessage="State" />
                            </label>
                            <div className="checkout-form-fields">
                                <Field
                                    name={`${this.props.fieldNamePrefix}[state_name]`}
                                    fieldNamePrefix={this.props.fieldNamePrefix}
                                    states={this.state.stateList.states || []}
                                    component={StateField}
                                    displayLoader={this.state.loadingStates}
                                    handleStateChange={this.handleStateChange}
                                    selectedStateCode={this.state.selectedStateCode}
                                    validate={validators.required}
                                    disabled={!this.isBilling() && ZoneService.isCurrentZoneCuba()}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        {this.props.selectedCountryIso === APP_DEFAULTS.countryIsoCuba ? (
                            <div className="checkout-form-row">
                                <label htmlFor="zipcode" className="checkout-form-label">
                                    <FormattedMessage id="zipCode" defaultMessage="Zipcode" />
                                </label>
                                <div className="checkout-form-fields">
                                    <Field
                                        name={`${this.props.fieldNamePrefix}[zipcode]`}
                                        fieldNamePrefix={this.props.fieldNamePrefix}
                                        component={ZipCodeField}
                                        zipcodes={this.state.zipcodesList.zipcodes}
                                        validate={[validators.required, validators.zipCode]}
                                    />
                                </div>
                            </div>
                        ) : (
                            <Field
                                className="form-control"
                                name={`${this.props.fieldNamePrefix}[zipcode]`}
                                component={FormField.inputFieldMarkup}
                                label={this.props.intl.formatMessage({ id: 'zipCode', defaultMessage: 'Zipcode' })}
                                type="text"
                                validate={[validators.required, validators.zipCode]}
                            />
                        )}
                    </div>
                    <div className="col-md-6">
                        <Field
                            className="form-control"
                            name={`${this.props.fieldNamePrefix}[phone]`}
                            component={FormField.inputFieldMarkup}
                            label={this.props.intl.formatMessage({ id: 'phone', defaultMessage: 'Phone' })}
                            type="text"
                            validate={[validators.required, validators.phone]}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default injectIntl(AddressFields);



/** WEBPACK FOOTER **
 ** ./src/components/checkout-steps/address/address-fields.jsx
 **/